import { useMemo, useState, type ReactElement } from 'react';
import AdminLayout from '../../components/AdminLayout';
import BulkJobModal from '../../components/BulkJobModal';
import {
  previewBulkRecalculate,
  submitBulkRecalculate,
  type BulkRecalcPreview,
} from '../../lib/jobs';

/**
 * Admin → Operations → Bulk recalculate dates.
 *
 * Fan-out form of the per-event "Recalculate date" button: re-runs the
 * extraction pipeline (postedAt-anchored) for every raw post INGESTED within
 * a date window. Useful after a date-detection fix to repair items that were
 * extracted before the fix landed.
 *
 * Flow: pick a window (or a quick preset) + optional source filter → Preview
 * (counts matches, no writes) → Run (creates a `recalc-date` bulk job and
 * opens the progress modal). The job runs server-side; closing the tab does
 * not abort it.
 */

/** YYYY-MM-DD for an offset-from-today date, in local time. */
function isoDateDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All sources' },
  { value: 'instagram', label: 'Instagram (venue)' },
  { value: 'series', label: 'Instagram (series)' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'website', label: 'Website' },
  { value: 'rss', label: 'RSS' },
  { value: 'ical', label: 'iCal' },
  { value: 'eventbrite', label: 'Eventbrite' },
  { value: 'extension', label: 'Extension capture' },
  { value: 'manual', label: 'Manual / compose' },
];

export default function AdminBulkRecalcPage(): ReactElement {
  const [fromDate, setFromDate] = useState<string>(() => isoDateDaysAgo(14));
  const [toDate, setToDate] = useState<string>(() => isoDateDaysAgo(0));
  const [sourceType, setSourceType] = useState<string>('all');
  const [onlyWithExtracted, setOnlyWithExtracted] = useState<boolean>(false);

  const [preview, setPreview] = useState<BulkRecalcPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<{ jobPk: string; jobId: string } | null>(null);

  // A change to any input invalidates the last preview so the count shown
  // always matches the window the user is about to submit.
  const previewKey = useMemo(
    () => `${fromDate}|${toDate}|${sourceType}|${onlyWithExtracted}`,
    [fromDate, toDate, sourceType, onlyWithExtracted]
  );
  const [previewKeyAt, setPreviewKeyAt] = useState<string>('');
  const previewIsStale = preview !== null && previewKeyAt !== previewKey;

  function applyPreset(days: number): void {
    setFromDate(isoDateDaysAgo(days));
    setToDate(isoDateDaysAgo(0));
    setPreview(null);
    setError(null);
  }

  async function onPreview(): Promise<void> {
    setPreviewing(true);
    setError(null);
    try {
      const res = await previewBulkRecalculate({
        fromDate,
        toDate,
        sourceType,
        onlyWithExtracted,
      });
      setPreview(res);
      setPreviewKeyAt(previewKey);
    } catch (e) {
      setPreview(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewing(false);
    }
  }

  async function onRun(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitBulkRecalculate({
        fromDate,
        toDate,
        sourceType,
        onlyWithExtracted,
      });
      setJob({ jobPk: res.jobPk, jobId: res.jobId });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const canRun =
    !!preview &&
    !previewIsStale &&
    preview.count > 0 &&
    !preview.overLimit &&
    !submitting;

  return (
    <AdminLayout>
      <div className="mb-3">
        <h1 className="h3 mb-1">Bulk recalculate dates</h1>
        <p className="text-body-secondary small mb-0">
          Re-run the extraction pipeline for every post <strong>ingested</strong> in a date window,
          re-anchoring each event&rsquo;s date to the original post date. This is the bulk form of the
          per-event &ldquo;Recalculate date&rdquo; button &mdash; most useful for repairing Instagram
          posts whose dates were computed before a date-detection fix. The job runs in the background;
          you can close this tab once it starts.
        </p>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <fieldset>
            <legend className="h6">Window &amp; filters</legend>

            <div className="d-flex flex-wrap gap-2 mb-3" role="group" aria-label="Quick window presets">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyPreset(7)}>
                Last 7 days
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyPreset(14)}>
                Last 2 weeks
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => applyPreset(30)}>
                Last 30 days
              </button>
            </div>

            <div className="row g-3">
              <div className="col-sm-6 col-md-3">
                <label htmlFor="recalc-from" className="form-label">
                  Ingested from
                </label>
                <input
                  id="recalc-from"
                  type="date"
                  className="form-control"
                  value={fromDate}
                  max={toDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setError(null);
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-3">
                <label htmlFor="recalc-to" className="form-label">
                  Ingested to
                </label>
                <input
                  id="recalc-to"
                  type="date"
                  className="form-control"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setError(null);
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-3">
                <label htmlFor="recalc-source" className="form-label">
                  Source
                </label>
                <select
                  id="recalc-source"
                  className="form-select"
                  value={sourceType}
                  onChange={(e) => {
                    setSourceType(e.target.value);
                    setError(null);
                  }}
                >
                  {SOURCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-md-3 d-flex align-items-end">
                <div className="form-check">
                  <input
                    id="recalc-only-extracted"
                    type="checkbox"
                    className="form-check-input"
                    checked={onlyWithExtracted}
                    onChange={(e) => {
                      setOnlyWithExtracted(e.target.checked);
                      setError(null);
                    }}
                  />
                  <label htmlFor="recalc-only-extracted" className="form-check-label">
                    Only items that produced events
                  </label>
                </div>
              </div>
            </div>
          </fieldset>

          <div className="d-flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => void onPreview()}
              disabled={previewing}
            >
              {previewing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                  Counting…
                </>
              ) : (
                <>
                  <i className="bi bi-search me-1" aria-hidden="true" />
                  Preview matches
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void onRun()}
              disabled={!canRun}
              title={
                !preview
                  ? 'Preview the matches first'
                  : previewIsStale
                    ? 'Filters changed — preview again'
                    : preview.overLimit
                      ? `Too many items (${preview.count}) — narrow the window`
                      : preview.count === 0
                        ? 'No matching items'
                        : undefined
              }
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" aria-hidden="true" />
                  Starting…
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-repeat me-1" aria-hidden="true" />
                  Run recalculation
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {preview && (
        <div
          className={`alert ${preview.overLimit ? 'alert-warning' : preview.count === 0 ? 'alert-secondary' : 'alert-info'}`}
          role="status"
          aria-live="polite"
        >
          {previewIsStale ? (
            <span>Filters changed since this preview &mdash; click <strong>Preview matches</strong> again.</span>
          ) : preview.count === 0 ? (
            <span>No ingested items match that window.</span>
          ) : (
            <>
              <strong>{preview.count.toLocaleString()}</strong> item{preview.count === 1 ? '' : 's'} ingested{' '}
              between <strong>{preview.fromIso.slice(0, 10)}</strong> and{' '}
              <strong>{preview.toIso.slice(0, 10)}</strong>
              {preview.sourceType !== 'all' && (
                <>
                  {' '}
                  from <strong>{preview.sourceType}</strong>
                </>
              )}{' '}
              will be recalculated.
              {preview.count > 0 && (
                <div className="mt-1 text-body-secondary">
                  Each item re-runs one extraction (~$0.001&ndash;0.005), so this run costs roughly{' '}
                  <strong>
                    ${(preview.count * 0.001).toFixed(2)}&ndash;${(preview.count * 0.005).toFixed(2)}
                  </strong>{' '}
                  in LLM spend.
                </div>
              )}
              {preview.overLimit && (
                <div className="mt-1">
                  That exceeds the {preview.maxPerJob}-per-job cap. Narrow the date window or pick a single
                  source, then preview again.
                </div>
              )}
            </>
          )}
        </div>
      )}

      <BulkJobModal
        jobPk={job?.jobPk}
        jobId={job?.jobId}
        onClose={() => setJob(null)}
      />
    </AdminLayout>
  );
}
